import moment from 'moment';

/**
 * Parses a date into a day-truncated UTC moment, or null when it is missing/invalid.
 * UTC keeps this deterministic regardless of the machine's local timezone: these values
 * are calendar dates (stored at UTC midnight, see ProductAddon.js's `.tz('UTC', true)`),
 * not points in time, so parsing them in local time could shift the calendar day.
 */
const toStartOfDay = (date) => {
	if (!date) {
		return null;
	}

	const parsed = moment.utc(date);

	return parsed.isValid() ? parsed.startOf('day') : null;
};

/**
 * Mirrors the backend's inclusive end-date rule (SYN-3396): the end date is the last day the
 * license works, so every duration type subtracts one day.
 * - months/years: start + N months|years, minus 1 day
 * - days: start + N days, minus 1 day (a 30-day license from 1 October ends on 30 October)
 * Returns null for a missing/invalid start, a non-positive/NaN duration, or an unknown durationType.
 */
export const computeAddonEndDate = (startDate, duration, durationType) => {
	const start = toStartOfDay(startDate);
	const numericDuration = Number(duration);

	if (!start || !Number.isFinite(numericDuration) || numericDuration <= 0) {
		return null;
	}

	switch (durationType) {
		case 'months':
			return start.clone().add(numericDuration, 'months').subtract(1, 'day');
		case 'years':
			return start.clone().add(numericDuration, 'years').subtract(1, 'day');
		case 'days':
			return start.clone().add(numericDuration, 'days').subtract(1, 'day');
		default:
			return null;
	}
};

/**
 * Mirrors the backend's `isExpired` rule: a license works through the whole of its end day
 * in UTC, so it is expired only from 00:00:00 UTC of the day after `endDate`. Only the
 * calendar day of `endDate` counts (a started start-on-activation license keeps the
 * activation's time of day). Compared in UTC so the answer does not depend on the browser's
 * time zone. Returns false for a missing/invalid end date (forever or not-yet-started license).
 */
export const isAddonExpired = (endDate, now) => {
	const end = toStartOfDay(endDate);

	if (!end) {
		return false;
	}

	const today = (now ? moment.utc(now) : moment.utc()).startOf('day');

	return today.isAfter(end);
};

/**
 * Mirrors the backend's overlap rule (addon_overlap.py:195-216):
 * - both startOnActivation -> overlap
 * - both dated -> day-truncated inclusive comparison (touching periods overlap)
 * - one startOnActivation + one dated -> no overlap
 */
export const doAddonPeriodsOverlap = (periodA, periodB) => {
	if (!periodA || !periodB) {
		return false;
	}

	if (periodA.startOnActivation && periodB.startOnActivation) {
		return true;
	}

	if (periodA.startOnActivation || periodB.startOnActivation) {
		return false;
	}

	const startA = toStartOfDay(periodA.startDate);
	const endA = toStartOfDay(periodA.endDate);
	const startB = toStartOfDay(periodB.startDate);
	const endB = toStartOfDay(periodB.endDate);

	if (!startA || !endA || !startB || !endB) {
		return false;
	}

	const latestStart = moment.max(startA, startB);
	const earliestEnd = moment.min(endA, endB);

	return !latestStart.isAfter(earliestEnd);
};

/**
 * Groups add-on rows by addonId (rows with an empty addonId are ignored, they can never
 * conflict) and returns every conflicting pair within a group. Each side of a pair carries
 * the row's original index so callers can attach a validation error to the right row.
 */
export const findAddonPeriodOverlaps = (addons) => {
	const grouped = {};

	(addons || []).forEach((addon, index) => {
		if (!addon || !addon.addonId) {
			return;
		}

		if (!grouped[addon.addonId]) {
			grouped[addon.addonId] = [];
		}

		grouped[addon.addonId].push({
			index,
			addon,
			period: {
				startOnActivation: !!addon.startOnActivation,
				startDate: addon.startDate,
				// Always a moment instance (or null) here, whichever source it comes from, so
				// callers do not have to duck-type a raw ISO string vs. an already-parsed moment.
				endDate: addon.endDate != null ? toStartOfDay(addon.endDate) : computeAddonEndDate(addon.startDate, addon.duration, addon.durationType)
			}
		});
	});

	const overlaps = [];

	Object.values(grouped).forEach((rows) => {
		for (let i = 0; i < rows.length; i++) {
			for (let j = i + 1; j < rows.length; j++) {
				if (doAddonPeriodsOverlap(rows[i].period, rows[j].period)) {
					overlaps.push({ a: rows[i], b: rows[j] });
				}
			}
		}
	});

	return overlaps;
};

/**
 * Applies a single row's change to a productAddons array, the same way ProductForm's
 * changeAddon used to inline it: reconciles the changed row by uuid (every other row is
 * returned untouched, same object identity), defaults a renewal's start date, and
 * recomputes endDate when the row's date fields changed. Pure - no React/redux, no
 * mutation of productAddons or changedAddon.
 */
export const applyAddonRowChange = (productAddons, changedAddon) => {
	return productAddons.map((existingAddon) => {
		if (existingAddon.uuid !== changedAddon.uuid) {
			return existingAddon;
		}

		let updatedAddon = { ...changedAddon };

		// Default a renewal's start date to the day after the latest existing period for the
		// same add-on, so the correct value is the default instead of a trap. Only applies when
		// the operator just picked/changed the add-on itself - not on every edit, or a cleared
		// datepicker (ProductAddon.js sends startDate: '' on clear) would be instantly refilled.
		const addonIdChanged = existingAddon.addonId !== updatedAddon.addonId;

		if (addonIdChanged && updatedAddon.addonId && !updatedAddon.startDate && !updatedAddon.startOnActivation) {
			const latestEndDate = productAddons.reduce((latest, otherAddon) => {
				if (otherAddon.uuid === updatedAddon.uuid || otherAddon.addonId !== updatedAddon.addonId) {
					return latest;
				}

				const otherEndDate = otherAddon.endDate ? moment.utc(otherAddon.endDate) : computeAddonEndDate(otherAddon.startDate, otherAddon.duration, otherAddon.durationType);

				if (!otherEndDate || !otherEndDate.isValid()) {
					return latest;
				}

				return (!latest || otherEndDate.isAfter(latest)) ? otherEndDate : latest;
			}, null);

			if (latestEndDate) {
				// latestEndDate is already a UTC moment (both sources are moment.utc-based),
				// so no `.tz('UTC', true)` is needed here - and that call only works via
				// moment-timezone's webpack ProvidePlugin, which mocha does not have.
				updatedAddon.startDate = latestEndDate.clone().add(1, 'day').startOf('day').toISOString();
			}
		}

		// Rows loaded from the server keep their original endDate even after the operator
		// edits startDate/duration/durationType/startOnActivation, which then contradicts the
		// UI's own expiry logic. Only recompute it here, for the row the operator actually just
		// changed. A row switched to startOnActivation never keeps a computed endDate, even if
		// a stale startDate is still sitting in state.
		const dateFieldsChanged = existingAddon.startDate !== updatedAddon.startDate || existingAddon.duration !== updatedAddon.duration || existingAddon.durationType !== updatedAddon.durationType || existingAddon.startOnActivation !== updatedAddon.startOnActivation;

		if (dateFieldsChanged) {
			const computedEndDate = updatedAddon.startOnActivation ? null : computeAddonEndDate(updatedAddon.startDate, updatedAddon.duration, updatedAddon.durationType);
			updatedAddon.endDate = computedEndDate ? computedEndDate.toISOString() : undefined;
		}

		return updatedAddon;
	});
};

export default { computeAddonEndDate, isAddonExpired, doAddonPeriodsOverlap, findAddonPeriodOverlaps, applyAddonRowChange };
